import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { toast } from 'sonner'

import { NameStep } from './components/NameStep'
import { SelfAssessmentStep } from './components/SelfAssessmentStep'

import { useAuth, useUserProfile } from '../../store/hooks'

export function Onboarding() {
  const { authUser } = useAuth()
  const { profile, updateProfile } = useUserProfile()

  const initialName = profile?.name ?? ''
  const initialSelfAssessmentEnabled = profile?.selfAssessmentEnabled ?? null

  const [step, setStep] = useState(initialName.length > 0 ? 1 : 0)
  const [name, setName] = useState(initialName)
  const [selfAssessmentEnabled, setSelfAssessmentEnabled] = useState<
    boolean | null
  >(initialSelfAssessmentEnabled)

  const steps: Array<{
    id: string
    component: React.ComponentType<any>
    props?: Record<string, any>
  }> = [
    { id: 'name', component: NameStep, props: { name, setName } },
    {
      id: 'self-assessment',
      component: SelfAssessmentStep,
      props: {
        name,
        selfAssessmentEnabled,
        setSelfAssessmentEnabled,
      },
    },
  ]

  const handleNext = () => {
    if (!authUser) return

    const { id } = steps[step]
    switch (id) {
      case 'name':
        updateProfile({
          name,
        })
        break
      case 'self-assessment':
        if (selfAssessmentEnabled === null) return
        updateProfile({
          selfAssessmentEnabled,
        })
        if (selfAssessmentEnabled) {
          toast.success('Self Assessment deadlines added to your calendar')
        }
        break
    }

    if (step < steps.length - 1) {
      setStep(step + 1)
    }
  }

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-lg">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            {steps.map(
              (s, index) =>
                step === index && <s.component key={index} {...s.props} />,
            )}
          </motion.div>
        </AnimatePresence>
        <div className="mt-6 flex justify-between">
          <button
            onClick={handleBack}
            disabled={step === 0}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
          >
            Back
          </button>
          <button
            onClick={handleNext}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}
